import React, { Component, Fragment } from 'react';
import Input from '../shared/input';
import DatePicker from '~/components/shared/date_picker';
import Counter from '~/components/shared/counter';
import Select from '~/components/shared/select';
import I18n from '~/utils/translations';
import RadioButtonInput from '../shared/radio_button_input';
import _isEmpty from 'lodash/isEmpty';

const incomeSourcesToOptions = (values) =>
  values.map((value) => {
    return {
      value,
      label: I18n.t(`pre_qualification_questions.income_source_types.${value}`),
    };
  });

export default class CustomPreQualificationQuestions extends Component {
  constructor(props) {
    super(props);
    this.state = {
      ...this.getInitialAnswerStates(),
      errors: this.setInitialErrors(),
    };
  }

  componentDidUpdate() {
    if (this.props.triggerPreQualValidation) {
      this.validateRequiredFields();
      this.props.onValidationComplete();
    }
  }

  getInitialAnswerStates() {
    const { questions, answers } = this.props;
    const initialAnswerStates = {};

    questions.forEach((question) => {
      if (_isEmpty(answers)) {
        if (question.response_type === 'integer') {
          initialAnswerStates[question.key] = 1;
        } else {
          initialAnswerStates[question.key] = null;
        }
      } else {
        const answer = answers.find((a) => a.question.key === question.key);

        if (question.response_type === 'integer') {
          initialAnswerStates[question.key] = answer ? answer.answer : 1;
        } else if (question.response_type === 'income_sources' && answer) {
          initialAnswerStates[question.key] =
            typeof answer.answer === 'string' ? JSON.parse(answer.answer) : answer.answer;
        } else {
          initialAnswerStates[question.key] = answer ? answer.answer : null;
        }
      }

      this.props.onInputChange(question.key, initialAnswerStates[question.key]);
    });

    return initialAnswerStates;
  }

  setInitialErrors() {
    const questions = this.props.questions;
    const errors = {};

    questions.forEach((question) => {
      errors[question.key] = [];
    });

    return errors;
  }

  validateRequiredFields = () => {
    const errors = {};
    this.props.questions.some((requiredQuestion) => {
      if (requiredQuestion.required) {
        const answer = this.state[requiredQuestion.key];
        if (answer == null) {
          errors[requiredQuestion.key] = [I18n.t('pre_qualification_questions.errors.' + requiredQuestion.key)];
        }
      }
    });
    this.setState({ errors: errors });
  };

  updateErrors = (key, valuePresent, required) => {
    if (!valuePresent && required) {
      this.setState({ errors: { ...this.state.errors, [key]: [I18n.t('pre_qualification_questions.errors.' + key)] } });
    } else {
      this.setState({ errors: { ...this.state.errors, [key]: [] } });
    }
  };

  handleInputChange = (e, key, required) => {
    const { value } = e.target;

    this.setState({ [key]: value }, () => this.props.onInputChange(key, value));

    this.updateErrors(key, value != null, required);
  };

  handleCurrencyChange = (e, key, required) => {
    const rawValue = e.target.value.replace(/,/g, ''); // Remove commas from the input

    // Check if the raw value is a valid integer before updating the state
    if (/^\d*$/.test(rawValue)) {
      const intValue = rawValue === '' ? null : parseInt(rawValue, 10);

      if (key !== 'income_sources') {
        this.setState({ [key]: intValue }, () => this.props.onInputChange(key, intValue));
      }

      this.updateErrors(key, intValue != null, required);
    }
  };

  handleBooleanChange = (value, key, required) => {
    this.setState({ [key]: value }, () => this.props.onInputChange(key, value));

    this.updateErrors(key, value != null, required);
  };

  onMainSourcesOfIncomeChange = (options, key, required) => {
    const selectedIncomeSources = options.target.value.map((option) => option.value);

    this.setState({ [key]: selectedIncomeSources }, () => {
      this.props.onInputChange(key, JSON.stringify(selectedIncomeSources));
    });

    this.updateErrors(key, selectedIncomeSources.length > 0, required);
  };

  onCountChange = (delta, min, key) => {
    this.setState(
      (prevState) => {
        const newCount = Math.max(min, (prevState[key] || 1) + delta);
        return { [key]: newCount };
      },
      () => this.props.onInputChange(key, this.state[key])
    );
  };

  renderInputs() {
    return this.props.questions.map((question) => {
      const isRequired = question.required === true;
      const optionalText = !isRequired ? ' (optional)' : '';

      return (
        <div key={question.order}>
          {question.response_type === 'date'
            ? this.renderDatePicker(question, optionalText)
            : question.response_type === 'integer'
            ? this.renderNumberInput(question, optionalText)
            : question.response_type === 'salary'
            ? this.renderSalary(question, optionalText)
            : question.response_type === 'income_sources'
            ? this.renderIncomeSource(question, optionalText)
            : question.response_type === 'bool'
            ? this.renderBoolean(question, optionalText)
            : null}
        </div>
      );
    });
  }

  renderDatePicker(question, optionalText) {
    const initialMonth = this.props.moveInDate
      ? new Date(this.props.moveInDate)
      : this.props.moveInAvailableFrom
      ? new Date(this.props.moveInAvailableFrom)
      : undefined;

    const value = this.state[question.key] ? new Date(this.state[question.key]) : undefined;

    return (
      <Fragment key={question.order}>
        <div className="about-you__move-in-date">{`${question.prompt}${optionalText}`}</div>
        <DatePicker
          dateFormat="YYYY-MM-DD"
          initialMonth={initialMonth}
          selectedDay={value}
          holidays={[]}
          numberOfMonths={1}
          onChange={(date) =>
            this.handleInputChange({ target: { name: question.key, value: date } }, question.key, question.required)
          }
          disabledDays={[
            { before: this.props.moveInAvailableFrom ? new Date(this.props.moveInAvailableFrom) : new Date() },
          ]}
          errors={this.state.errors[question.key]}
          isLondon
        />
      </Fragment>
    );
  }

  renderNumberInput(question, optionalText) {
    const quantity = this.state[question.key] || 1;

    return (
      <div className="about-you__people-count" key={question.order}>
        <Counter
          label={`${question.prompt}${optionalText}`}
          quantity={quantity}
          decreaseQuantity={() => this.onCountChange(-1, 1, question.key)}
          increaseQuantity={() => this.onCountChange(1, 1, question.key)}
          errors={this.state.errors[question.key]}
          leftAligned
        />
      </div>
    );
  }

  renderSalary(question, optionalText) {
    const intValue =
      this.state[question.key] !== null && this.state[question.key] !== undefined
        ? parseInt(this.state[question.key], 10)
        : null;

    const formattedValue = intValue !== null ? intValue.toLocaleString('en-UK') : '';

    return (
      <Fragment key={question.order}>
        <Input
          label={`${question.prompt}${optionalText}`}
          type="text"
          prepend={I18n.t('currency.symbol')}
          name="annual_household_income_amount"
          className="about-you__annual-income"
          onChange={(e) => this.handleCurrencyChange(e, question.key, question.required)}
          value={formattedValue}
          errors={this.state.errors[question.key]}
        />
      </Fragment>
    );
  }

  renderIncomeSource(question, optionalText) {
    const stateIncomeSources = this.state[question.key];
    const selectedIncomeSources = stateIncomeSources !== null ? incomeSourcesToOptions(stateIncomeSources) : undefined;

    return (
      <Fragment key={question.order}>
        <Select
          label={`${question.prompt}${optionalText}`}
          id="main_sources_of_income"
          name="main_sources_of_income"
          className="about-you__income-sources"
          options={incomeSourcesToOptions([
            'employed',
            'savings',
            'self_employed',
            'pension',
            'student',
            'unemployed',
            'benefits',
          ])}
          placeholder=""
          onChange={(value) =>
            this.onMainSourcesOfIncomeChange({ target: { name: question.key, value } }, question.key, question.required)
          }
          isSearchable={false}
          isMulti
          value={selectedIncomeSources}
          errors={this.state.errors[question.key]}
        />
      </Fragment>
    );
  }

  renderBoolean(question, optionalText) {
    const selectedOption = this.state[question.key] !== null ? (this.state[question.key] ? 'Yes' : 'No') : undefined;

    return (
      <Fragment key={question.order}>
        <div className="about-you__boolean">{`${question.prompt}${optionalText}`}</div>
        <div key={question.order} className="preferences-pane__option">
          <RadioButtonInput
            label={question.prompt}
            options={['Yes', 'No']}
            name={`${question.key}`}
            onChange={(value) => this.handleBooleanChange(value, question.key, question.required)}
            selectedOption={selectedOption}
            errors={this.state.errors[question.key]}
          />
        </div>
      </Fragment>
    );
  }

  render() {
    return <Fragment>{this.renderInputs()}</Fragment>;
  }
}
